import { DateTime } from '@ksyos/date-time';
import {
    Box,
    EmptyState,
    Link,
    Pagination,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useFundusScreeningManualImportLogs } from '@frontend/components/pages/fundusScreening/fundusScreeningManualImport/logs/useFundusScreeningManualImportLogs';
import { getSelectedLanguage } from '@frontend/translate';

export function FundusScreeningManualImportLogsTable() {
    const translate = getSelectedLanguage();

    const { logs, isLoading, pagination, sorting } = useFundusScreeningManualImportLogs();

    return (
        <Box textAlign="center">
            {isLoading && <Spinner color="secondary" />}

            {!isLoading && logs.length > 0 && (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader
                                    isSortable={true}
                                    isSortHeader={sorting.by === 'importDate'}
                                    onClick={() => sorting.changeTo('importDate')}
                                    sortDirection={sorting.direction}
                                >
                                    {translate.fundusScreening.manualImport.logbook.tableColumns.importDate}
                                </TableHeader>
                                <TableHeader
                                    isSortable={true}
                                    isSortHeader={sorting.by === 'thirdParty.name'}
                                    onClick={() => sorting.changeTo('thirdParty.name')}
                                    sortDirection={sorting.direction}
                                >
                                    {translate.fundusScreening.manualImport.logbook.tableColumns.thirdPartyName}
                                </TableHeader>
                                <TableHeader
                                    isSortable={true}
                                    isSortHeader={sorting.by === 'importedFileName'}
                                    onClick={() => sorting.changeTo('importedFileName')}
                                    sortDirection={sorting.direction}
                                >
                                    {translate.fundusScreening.manualImport.logbook.tableColumns.importedFilename}
                                </TableHeader>
                                <TableHeader
                                    isSortable={true}
                                    isSortHeader={sorting.by === 'group.name'}
                                    onClick={() => sorting.changeTo('group.name')}
                                    sortDirection={sorting.direction}
                                >
                                    {translate.fundusScreening.manualImport.logbook.tableColumns.careGroupName}
                                </TableHeader>
                                <TableHeader
                                    isSortable={true}
                                    isSortHeader={sorting.by === 'invitationType'}
                                    onClick={() => sorting.changeTo('invitationType')}
                                    sortDirection={sorting.direction}
                                >
                                    {translate.fundusScreening.manualImport.logbook.tableColumns.invitationType}
                                </TableHeader>
                                <TableHeader
                                    isSortable={true}
                                    isSortHeader={sorting.by === 'modifierPractitioner.nameFamily'}
                                    onClick={() => sorting.changeTo('modifierPractitioner.nameFamily')}
                                    sortDirection={sorting.direction}
                                >
                                    {translate.fundusScreening.manualImport.logbook.tableColumns.importedBy}
                                </TableHeader>
                                <TableHeader>
                                    {translate.fundusScreening.manualImport.logbook.tableColumns.download}
                                </TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map((log) => (
                                <TableRow key={log.id}>
                                    <TableCell sx={{ fontWeight: 500 }}>
                                        {DateTime.from(log.importDate).format('YYYY-MM-DD')}
                                    </TableCell>
                                    <TableCell>
                                        {
                                            translate.fundusScreening.manualImport.thirdParty[
                                                log.thirdPartyName as keyof typeof translate.fundusScreening.manualImport.thirdParty
                                            ]
                                        }
                                    </TableCell>
                                    <TableCell>{log.importedFileName}</TableCell>
                                    <TableCell>{log.careGroupName}</TableCell>
                                    <TableCell>
                                        {translate.fundusScreening.manualImport.inviteType[log.invitationType]}
                                    </TableCell>
                                    <TableCell>{log.importedBy}</TableCell>
                                    <TableCell>
                                        <Link
                                            color="subtle"
                                            iconRight="download"
                                            to={log.downloadUrl}
                                            variant="link"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {pagination.hasEnoughItemsToPaginate && (
                        <Pagination
                            {...pagination.options}
                            onChange={pagination.handleChange}
                        />
                    )}
                </TableContainer>
            )}
            {!isLoading && !logs.length && (
                <EmptyState
                    icon="searchresults"
                    subtitle=""
                    title={translate.fundusScreening.manualImport.logbook.emptyState}
                    variant="centered"
                />
            )}
        </Box>
    );
}
